/**
 * MQTT Service — Singleton (main thread). Kit19 SSO ecosystem.
 * A single shared broker connection used to publish/receive cross-app
 * logout events.
 *
 * PORTABLE: copy as-is. Set the broker via runtime config / env (see below);
 * the defaults match the shared Kit19 broker.
 */

import mqtt from 'mqtt';

// Resolve broker config: runtime config (post-deploy, editable in public/config.js)
// → build-time env var → default. Lets the broker be repointed without rebuilding.
const resolveMqttConfig = () => {
  const rc = (typeof window !== 'undefined' && window.RUNTIME_CONFIG) || {};
  return {
    url: rc.MQTT_URL || process.env.REACT_APP_MQTT_URL || 'wss://mqtt.kit19.com:8084/mqtt',
    username: rc.MQTT_USERNAME || process.env.REACT_APP_MQTT_USERNAME || 'netadmin',
    password: rc.MQTT_PASSWORD || process.env.REACT_APP_MQTT_PASSWORD || 'admin'
  };
};

let client = null;
let isConnecting = false;
let isConnected = false;
let logoutHandlers = [];
let connectionPromise = null;

/**
 * Initialize the MQTT client (singleton). Safe to call multiple times.
 */
export const initMqttWorker = () => {
  if (client && client.connected) {
    return;
  }
  if (isConnecting) {
    return;
  }

  isConnecting = true;

  const clientId = `mqtt_${Date.now()}_${Math.random().toString(16).substring(2, 8)}`;
  const { url, username, password } = resolveMqttConfig();

  client = mqtt.connect(url, {
    clientId,
    username,
    password,
    keepalive: 30,
    clean: true,
    reconnectPeriod: 4000,
    connectTimeout: 30000
  });

  client.on('connect', () => {
    isConnecting = false;
    isConnected = true;
    console.log('[MQTT Service] Connected to broker');

    if (connectionPromise) {
      connectionPromise.resolve();
      connectionPromise = null;
    }
  });

  client.on('reconnect', () => {
    console.log('[MQTT Service] Attempting to reconnect...');
  });

  client.on('offline', () => {
    isConnected = false;
  });

  client.on('close', () => {
    isConnected = false;
  });

  client.on('error', (error) => {
    console.error('[MQTT Service] Error:', error);
    isConnecting = false;
    isConnected = false;
  });

  client.on('message', (topic, message) => {
    try {
      const data = JSON.parse(message.toString());

      // Route cross-app logout events to registered logout handlers.
      if (topic.startsWith('kit19/logout/') && data.type === 'LOGOUT_EVENT') {
        logoutHandlers.forEach((handler) => {
          try {
            handler(data);
          } catch (e) {
            console.error('[MQTT Service] Logout handler error:', e);
          }
        });
      }
    } catch (error) {
      console.error('[MQTT Service] Message parse error:', error);
    }
  });
};

// Resolve once the client is connected (or reject after a timeout).
const waitForConnection = () => {
  if (isConnected) {
    return Promise.resolve();
  }
  if (connectionPromise) {
    return connectionPromise.promise;
  }

  let resolve, reject;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  connectionPromise = { promise, resolve, reject };

  setTimeout(() => {
    if (connectionPromise) {
      connectionPromise.reject(new Error('Connection timeout'));
      connectionPromise = null;
    }
  }, 15000);

  return promise;
};

// Subscribe to a topic (waits for the connection first).
export const subscribeTopic = async (topic) => {
  if (!client) {
    throw new Error('MQTT client not initialized');
  }
  await waitForConnection();

  return new Promise((resolve, reject) => {
    client.subscribe(topic, (err) => {
      if (!err) {
        console.log('[MQTT Service] Subscribed to:', topic);
        resolve(true);
      } else {
        reject(err);
      }
    });
  });
};

// Publish a message (waits for the connection first). `qos` is a number.
export const publishMessage = async (topic, message, qos = 1) => {
  if (!client) {
    throw new Error('MQTT client not initialized');
  }
  await waitForConnection();

  return new Promise((resolve, reject) => {
    const messageStr = typeof message === 'string' ? message : JSON.stringify(message);
    client.publish(topic, messageStr, { qos }, (err) => {
      if (!err) {
        console.log('[MQTT Service] Published to:', topic);
        resolve(true);
      } else {
        reject(err);
      }
    });
  });
};

// Register a logout-event handler. Returns an unsubscribe function.
export const onLogoutEvent = (handler) => {
  logoutHandlers.push(handler);
  return () => {
    const index = logoutHandlers.indexOf(handler);
    if (index > -1) {
      logoutHandlers.splice(index, 1);
    }
  };
};

// Current connection status.
export const getMqttStatus = () => {
  if (!client) {
    return { status: 'not-initialized', connected: false };
  }
  return {
    status: isConnected ? 'connected' : (isConnecting ? 'connecting' : 'disconnected'),
    connected: isConnected,
    isConnecting
  };
};

// Disconnect and reset the client.
export const disconnectMqtt = () => {
  if (client) {
    client.end();
    client = null;
    isConnected = false;
    isConnecting = false;
    logoutHandlers = [];
    connectionPromise = null;
  }
};

const mqttServiceExport = {
  initMqttWorker,
  subscribeTopic,
  publishMessage,
  onLogoutEvent,
  getMqttStatus,
  disconnectMqtt
};

export default mqttServiceExport;
